import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, addDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { isSuperAdmin } from '../utils/roleManager';
import { ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import WaveformTrimmer from '../components/WaveformTrimmer';

async function callAdminFunction(name, data) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const fns = getFunctions(getApp());
  const fn = httpsCallable(fns, name);
  return fn(data);
}

function SuperAdminUserRecordings() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const userIsSuperAdmin = isSuperAdmin(currentUser);
  const audioRef = useRef(null);

  const [user, setUser] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [expandedTrimId, setExpandedTrimId] = useState(null);
  const [trimSavingId, setTrimSavingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const userSnap = await getDoc(doc(db, 'users', userId));
      setUser(userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null);

      const recQuery = query(collection(db, 'recordings'), where('userId', '==', userId));
      const recSnap = await getDocs(recQuery);
      const all = recSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => {
        const bookCmp = (a.book || '').localeCompare(b.book || '');
        if (bookCmp !== 0) return bookCmp;
        if ((a.chapter || 0) !== (b.chapter || 0)) return (a.chapter || 0) - (b.chapter || 0);
        return (a.verse || 0) - (b.verse || 0);
      });
      setRecordings(all);
    } catch (error) {
      console.error('Error loading recordings:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userIsSuperAdmin) {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsSuperAdmin, navigate, userId]);

  const playRecording = (rec) => {
    if (playingId === rec.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = rec.audioURL || rec.audioUrl || '';
      audioRef.current.play().catch(() => {});
    }
    setPlayingId(rec.id);
  };

  // Manual, waveform-driven trim only — same trimRequests pipeline a user
  // uses on their own recordings from Profile. There is no auto-trim; trim
  // points always come from a human dragging the region on the waveform.
  const handleApplyTrim = async (rec, trimStart, trimEnd) => {
    setTrimSavingId(rec.id);
    try {
      const reqRef = await addDoc(collection(db, 'trimRequests'), {
        recordingId: rec.id,
        requestedBy: currentUser.uid,
        trimStart,
        trimEnd,
        status: 'pending',
      });
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => { unsub(); reject(new Error('Timed out')); }, 120000);
        const unsub = onSnapshot(reqRef, (snap) => {
          const status = snap.data()?.status;
          if (status === 'done') { clearTimeout(timeout); unsub(); resolve(); }
          else if (status === 'error') { clearTimeout(timeout); unsub(); reject(new Error(snap.data()?.error || 'Trim failed')); }
        });
      });
      const recDoc = await getDoc(doc(db, 'recordings', rec.id));
      if (recDoc.exists()) {
        setRecordings((prev) => prev.map((r) => (r.id === rec.id ? { id: recDoc.id, ...recDoc.data() } : r)));
      }
      setExpandedTrimId(null);
    } catch (error) {
      alert(`Failed to trim: ${error.message}`);
    } finally {
      setTrimSavingId(null);
    }
  };

  const handleRestore = async (rec) => {
    if (!window.confirm(`Restore the original recording for ${rec.passage}? This will undo any trimming.`)) return;
    setRestoringId(rec.id);
    try {
      const reqRef = await addDoc(collection(db, 'restoreRequests'), {
        recordingId: rec.id,
        requestedBy: currentUser.uid,
        status: 'pending',
      });
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => { unsub(); reject(new Error('Timed out')); }, 180000);
        const unsub = onSnapshot(reqRef, (snap) => {
          const status = snap.data()?.status;
          if (status === 'done') { clearTimeout(timeout); unsub(); resolve(); }
          else if (status === 'error') { clearTimeout(timeout); unsub(); reject(new Error(snap.data()?.error || 'Restore failed')); }
        });
      });
      const recDoc = await getDoc(doc(db, 'recordings', rec.id));
      if (recDoc.exists()) {
        setRecordings((prev) => prev.map((r) => (r.id === rec.id ? { id: recDoc.id, ...recDoc.data() } : r)));
      }
    } catch (error) {
      alert(`Failed to restore: ${error.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const deleteOne = async (rec) => {
    if (!window.confirm(`Delete the recording for ${rec.passage}? This cannot be undone.`)) return;
    setDeletingIds((prev) => new Set([...prev, rec.id]));
    try {
      await callAdminFunction('adminDeleteRecording', { recordingId: rec.id });
      setRecordings((prev) => prev.filter((r) => r.id !== rec.id));
      if (playingId === rec.id) {
        audioRef.current?.pause();
        setPlayingId(null);
      }
      if (expandedTrimId === rec.id) setExpandedTrimId(null);
    } catch (error) {
      console.error('Error deleting recording:', error);
      alert(`Failed to delete recording: ${error.message}`);
    } finally {
      setDeletingIds((prev) => { const s = new Set(prev); s.delete(rec.id); return s; });
    }
  };

  if (!userIsSuperAdmin) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <audio ref={audioRef} onEnded={() => setPlayingId(null)} />

      <Link
        to={`/super-admin/users/${userId}`}
        className="text-cerulean hover:text-cerulean/80 text-sm font-medium flex items-center gap-1 w-fit"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Profile
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {user?.displayName || 'User'}'s Recordings
        </h1>
        <p className="text-sm text-gray-500">{user?.email}</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
        </div>
      )}

      {!loading && recordings.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <p className="font-medium">No recordings found for this user.</p>
        </div>
      )}

      {!loading && recordings.length > 0 && (
        <div className="space-y-2">
          {recordings.map((rec) => {
            const isDeleting = deletingIds.has(rec.id);
            const isPlaying = playingId === rec.id;
            const isExpanded = expandedTrimId === rec.id;
            return (
              <div
                key={rec.id}
                className={`rounded-xl border p-4 transition-colors ${
                  rec.trimmed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => playRecording(rec)}
                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                      isPlaying ? 'bg-cerulean text-white' : 'bg-gray-100 hover:bg-cerulean/10 text-gray-600 hover:text-cerulean'
                    }`}
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>

                  <button
                    onClick={() => setExpandedTrimId(isExpanded ? null : rec.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {rec.passage || `${rec.book} ${rec.chapter}:${rec.verse}`}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {rec.duration ? `${Math.round(rec.duration)}s` : ''} · {rec.createdAt?.toDate?.()?.toLocaleDateString() || '—'}
                      {rec.trimmed ? ' · Trimmed' : ''}
                    </p>
                  </button>

                  <button
                    onClick={() => setExpandedTrimId(isExpanded ? null : rec.id)}
                    disabled={isDeleting}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40 ${
                      isExpanded ? 'bg-cerulean text-white' : 'bg-cerulean/10 hover:bg-cerulean text-cerulean hover:text-white'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243zm0-5.758L6.343 6.343" />
                    </svg>
                    {isExpanded ? 'Close' : 'Edit Audio'}
                  </button>

                  <button
                    onClick={() => deleteOne(rec)}
                    disabled={isDeleting}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-500 text-red-600 hover:text-white rounded-lg transition-colors disabled:opacity-40"
                  >
                    {isDeleting ? (
                      <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrashIcon className="w-3.5 h-3.5" />
                    )}
                    Delete
                  </button>
                </div>

                {isExpanded && (
                  <div className="mt-3">
                    <WaveformTrimmer
                      audioUrl={rec.audioURL || rec.audioUrl}
                      onTrim={(start, end) => handleApplyTrim(rec, start, end)}
                      onCancel={() => setExpandedTrimId(null)}
                      isSaving={trimSavingId === rec.id}
                    />
                    {rec.trimmed && (
                      <div className="mt-2">
                        {restoringId === rec.id ? (
                          <div className="flex items-center gap-1.5 text-xs text-amber-600">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-500" />
                            Restoring original…
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRestore(rec)}
                            disabled={!!restoringId || !!trimSavingId}
                            className="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Restore original
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SuperAdminUserRecordings;
